import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { clubService } from '../../services/clubService';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const SetupCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 500px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #718096;
  margin-bottom: 32px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background-color: #f7fafc;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background-color: #f7fafc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fff5f5;
  border: 1px solid #fc8181;
  border-radius: 8px;
  padding: 12px 16px;
  color: #c53030;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #718096;
  margin-top: 4px;
`;

const AdminClubSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    affiliatedFA: '',
    address: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    // Validate club name (1-100 chars, not empty)
    if (!formData.name.trim()) {
      setError('Club name is required');
      return false;
    }
    if (formData.name.length > 100) {
      setError('Club name cannot exceed 100 characters');
      return false;
    }

    // Validate affiliated FA
    if (!formData.affiliatedFA.trim()) {
      setError('Affiliated FA is required');
      return false;
    }

    // Validate address
    if (!formData.address.trim()) {
      setError('Club address is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call POST /api/clubs
      await clubService.createClub({
        name: formData.name.trim(),
        affiliatedFA: formData.affiliatedFA.trim(),
        address: formData.address.trim(),
      });

      // On success, redirect to subscription selection page
      navigate('/administration-club/subscribe');
    } catch (err: any) {
      console.error('Club creation failed:', err);
      
      // Handle specific error codes
      if (err.response?.data?.error?.code === 'CLUB_NAME_INVALID') {
        setError(err.response.data.error.message);
      } else if (err.response?.data?.error?.code === 'CLUB_ALREADY_EXISTS') {
        setError('You already have a club associated with your account');
      } else if (err.response?.data?.error?.message) {
        setError(err.response.data.error.message);
      } else {
        setError('Failed to create club. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <SetupCard>
        <Title>Set Up Your Club</Title>
        <Subtitle>Let's get started by setting up your club identity</Subtitle>

        {error && (
          <ErrorMessage>
            <span>⚠️</span>
            <span>{error}</span>
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Club Name *</Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your club name"
              disabled={loading}
              maxLength={100}
              required
            />
            <HelpText>Maximum 100 characters</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="affiliatedFA">Affiliated Football Association *</Label>
            <Input
              id="affiliatedFA"
              name="affiliatedFA"
              type="text"
              value={formData.affiliatedFA}
              onChange={handleChange}
              placeholder="e.g., The FA, Scottish FA, Welsh FA"
              disabled={loading}
              required
            />
            <HelpText>The football association your club is registered with</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="address">Club Address *</Label>
            <TextArea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your club's full address"
              disabled={loading}
              required
            />
            <HelpText>Include street, city, and postcode</HelpText>
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Continue to Subscription'}
          </SubmitButton>
        </Form>
      </SetupCard>
    </Container>
  );
};

export default AdminClubSetupPage;
